import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { OnboardingStepIndicator } from "@/routes/ona-onboarding/OnboardingStepIndicator";
import { StepHeadline } from "@/routes/ona-onboarding/StepHeadline";
import { StepSubheader } from "@/routes/ona-onboarding/StepSubheader";
import { RepositorySelectorPopover } from "@/components/RepositorySelectorPopover";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import {
    onboardingRunnerAtom,
    onboardingStateAtom,
    parsedRepositoryUrlAtom,
    currentStepAtom,
    ONBOARDING_STEP,
    ONBOARDING_STEP_SLUG,
    lastSelectedScmAuthHostAtom,
} from "@/routes/ona-onboarding/onboarding-state";
import { useCallback, useEffect } from "react";
import { useParseContextURL } from "@/queries/runner-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useSegmentTrackError, useSegmentTrack } from "@/hooks/use-segment";
import { useNavigate } from "react-router-dom";
import { PageCategories, usePageTracking } from "@/hooks/use-page-tracking";

const selectedRepositoryUrlAtom = atom(
    (get) => get(onboardingStateAtom).selectedRepositoryUrl,
    (get, set, selectedRepositoryUrl: string | null) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), selectedRepositoryUrl });
    },
);

const canContinueAtom = atom((get) => {
    const parsedRepositoryUrl = get(parsedRepositoryUrlAtom);
    return Boolean(parsedRepositoryUrl?.git);
});

export const SelectRepository = () => {
    usePageTracking("Select Repository", PageCategories.OnaOnboarding);

    const store = useStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (!store.get(lastSelectedScmAuthHostAtom)) {
            navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.connectToScm}`, {
                replace: true,
            });
        }
    }, [store, navigate]);

    return (
        <div className="mx-0 mt-0 flex flex-col items-center justify-start p-8 pt-16 md:m-auto md:justify-center md:pt-8">
            <OnboardingStepIndicator className="mb-8" />
            <StepHeadline className="mb-2 mt-3">Choose a repository for Ona</StepHeadline>
            <StepSubheader className="mb-10">
                Ona will only access the repository you select. You can change this later.
            </StepSubheader>
            <OnboardingRepositorySelector />
            <ContinueButton className="mt-8" />
        </div>
    );
};

const OnboardingRepositorySelector = () => {
    const [selectedRepositoryUrl, setSelectedRepositoryUrl] = useAtom(selectedRepositoryUrlAtom);
    const setParsedRepositoryUrl = useSetAtom(parsedRepositoryUrlAtom);
    const store = useStore();
    const segmentTrack = useSegmentTrack();
    const onboardingRunnerQuery = useAtomValue(onboardingRunnerAtom);
    const lastSelectedScmAuthHost = useAtomValue(lastSelectedScmAuthHostAtom);
    const { toast } = useToast();
    const trackError = useSegmentTrackError();

    // Parse the selected repository URL
    const {
        data: parsedRepositoryUrl,
        isLoading: isParsing,
        error: parseError,
    } = useParseContextURL(
        onboardingRunnerQuery.type === "success" ? onboardingRunnerQuery.data.runnerId : undefined,
        selectedRepositoryUrl ?? undefined,
        { enabled: Boolean(selectedRepositoryUrl) },
    );

    // Update parsed repository URL when parsing completes
    useEffect(() => {
        if (parsedRepositoryUrl) {
            setParsedRepositoryUrl(parsedRepositoryUrl);
        }
    }, [parsedRepositoryUrl, setParsedRepositoryUrl]);

    // Handle parsing errors
    useEffect(() => {
        if (parseError) {
            setSelectedRepositoryUrl(null);
            setParsedRepositoryUrl(null);
            toast({
                title: "Failed to resolve selected repository",
                description: formatError(parseError),
            });
            trackError("onboarding_parse_repo_url_error", parseError);
        }
    }, [parseError, setSelectedRepositoryUrl, setParsedRepositoryUrl, trackError, toast]);

    const selectRepository = useCallback(
        (repoUrl: string, repoName: string) => {
            const currentSelectedUrl = store.get(selectedRepositoryUrlAtom);
            if (currentSelectedUrl !== repoUrl) {
                setSelectedRepositoryUrl(repoUrl);
                setParsedRepositoryUrl(null);

                // Track repository selection
                segmentTrack("onboarding_repository_selected", {
                    location: "onboarding_select_repository",
                    repository_name: repoName,
                    repository_url: repoUrl,
                });
            }
        },
        [setSelectedRepositoryUrl, setParsedRepositoryUrl, store, segmentTrack],
    );

    // Determine button label
    let buttonLabel = "Select repository";
    if (selectedRepositoryUrl) {
        if (isParsing) {
            buttonLabel = "Resolving repository…";
        } else if (parsedRepositoryUrl?.git?.repo) {
            buttonLabel = parsedRepositoryUrl.git.repo;
        }
    }

    if (onboardingRunnerQuery.type !== "success" || !lastSelectedScmAuthHost) {
        return null;
    }

    return (
        <RepositorySelectorPopover
            runnerId={onboardingRunnerQuery.data.runnerId}
            scmHost={lastSelectedScmAuthHost}
            value={selectedRepositoryUrl ?? undefined}
            onSelect={selectRepository}
            buttonLabel={buttonLabel}
            disabled={!!selectedRepositoryUrl && isParsing}
            trackingLocation="onboarding_select_repository"
            triggerClassName="mb-3"
        />
    );
};


const ContinueButton = ({ className }: { className?: string }) => {
    const canContinue = useAtomValue(canContinueAtom);
    const navigate = useNavigate();
    const setCurrentStep = useSetAtom(currentStepAtom);

    const onClick = () => {
        if (canContinue) {
            setCurrentStep(ONBOARDING_STEP[ONBOARDING_STEP_SLUG.getStartedWithTasks]);
            navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.getStartedWithTasks}`);
        }
    };

    return (
        <Button
            variant="primary"
            className={cn("h-10 px-8 py-2", { invisible: !canContinue }, className)}
            size="lg"
            disabled={!canContinue}
            onClick={onClick}
            data-tracking-id="continue-onboarding-select-repository-page"
        >
            Continue
        </Button>
    );
};
